import { useState, useEffect, useCallback } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useNavigate } from 'react-router-dom';
import { Plus, MapPin, Star, StickyNote, Trash2, Users, GripVertical, X, Mail, Loader2, Send, FileText } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { authFetch } from '../../utils/authFetch';
import PageHelp from '../../components/PageHelp';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STAGES = ['saved', 'contacted', 'accepted', 'in_process', 'offer_sent', 'unavailable', 'hired', 'archived'];

const STAGE_CONFIG = {
  saved:      { label: 'Saved',          topBorder: '#3b82f6', badgeCls: 'bg-blue-50 text-blue-700' },
  contacted:  { label: 'Interest Sent',  topBorder: '#f59e0b', badgeCls: 'bg-amber-50 text-amber-700' },
  accepted:   { label: 'Accepted',       topBorder: '#22c55e', badgeCls: 'bg-green-50 text-green-700' },
  in_process: { label: 'In Process',     topBorder: '#8b5cf6', badgeCls: 'bg-violet-50 text-violet-700' },
  offer_sent: { label: 'Offer Sent',     topBorder: '#c9a84c', badgeCls: 'bg-yellow-50 text-yellow-700' },
  unavailable:{ label: 'Unavailable',    topBorder: '#ef4444', badgeCls: 'bg-red-50 text-red-700' },
  hired:      { label: 'Hired',          topBorder: '#111111', badgeCls: 'bg-zinc-100 text-zinc-800' },
  archived:   { label: 'Archived',       topBorder: '#9ca3af', badgeCls: 'bg-zinc-50 text-zinc-500' },
};

// ─── Express Interest Modal ───────────────────────────────────────────────────

const OutreachModal = ({ item, onSend, onClose }) => {
  const c = item.candidate || {};
  const [jobRole, setJobRole] = useState(c.core_role || '');
  const [jobLocation, setJobLocation] = useState(c.location_city || '');
  const [customMessage, setCustomMessage] = useState('');
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!jobRole.trim()) { toast.error('Please enter the position you are hiring for'); return; }
    setSending(true);
    await onSend(item.id, { job_role: jobRole.trim(), job_location: jobLocation.trim(), custom_message: customMessage.trim() });
    setSending(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="outreach-modal">
      <div className="bg-white w-full max-w-lg border border-border" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-border flex items-start justify-between">
          <div>
            <h3 className="text-sm font-semibold" style={{ fontFamily: "'Bodoni Moda', Georgia, serif" }}>
              Express Interest via Flence
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              {c.full_name} · {[c.seniority, c.core_role].filter(Boolean).join(' ')}
            </p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground mt-0.5">
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Info note */}
          <div className="bg-amber-50 border border-amber-100 px-4 py-3">
            <p className="text-xs text-amber-800 leading-relaxed">
              A direct email will be sent to {c.full_name} from <strong>talent@fashionspyder.com</strong>. Their reply goes straight to your inbox. They can also decline via their Flence Recruitment Hub.
            </p>
          </div>

          {/* Position */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-widest mb-1.5">
              Position You're Hiring For <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={jobRole}
              onChange={e => setJobRole(e.target.value)}
              placeholder="e.g. Senior Womenswear Designer"
              className="w-full border border-border px-3 py-2 text-sm focus:outline-none focus:border-foreground"
              data-testid="outreach-job-role-input"
            />
          </div>

          {/* Location */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-widest mb-1.5">
              Location
            </label>
            <input
              type="text"
              value={jobLocation}
              onChange={e => setJobLocation(e.target.value)}
              placeholder="e.g. Milan, Italy"
              className="w-full border border-border px-3 py-2 text-sm focus:outline-none focus:border-foreground"
              data-testid="outreach-location-input"
            />
          </div>

          {/* Custom Message */}
          <div>
            <label className="block text-xs font-semibold uppercase tracking-widest mb-1.5">
              Personal Introduction <span className="text-red-500">*</span>
            </label>
            <p className="text-xs text-muted-foreground mb-2">This message is included in the email to the candidate. Be specific — mention what caught your attention.</p>
            <textarea
              value={customMessage}
              onChange={e => setCustomMessage(e.target.value)}
              placeholder="Tell them what specifically caught your attention about their work, and a brief note about the role and why you think it's a good fit..."
              className="w-full border border-border px-3 py-2 text-sm resize-none focus:outline-none focus:border-foreground"
              rows={4}
              data-testid="outreach-message-input"
            />
          </div>

          {/* Info block */}
          <div className="bg-secondary/30 px-4 py-3 text-xs text-muted-foreground leading-relaxed border border-border/40">
            <span className="font-semibold text-foreground">What happens next:</span> The candidate receives a direct email and can reply to it directly. Once they respond or accept on Flence, their contact details are unlocked in your pipeline under "Accepted".
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 flex gap-2">
          <Button
            onClick={handleSend}
            disabled={sending || !jobRole.trim() || !customMessage.trim()}
            className="flex-1 rounded-none bg-foreground text-background hover:bg-foreground/80 h-9 text-xs gap-1.5"
            data-testid="outreach-send-btn"
          >
            {sending ? (
              <><Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.5} /> Sending...</>
            ) : (
              <><Mail className="w-3.5 h-3.5" strokeWidth={1.5} /> Express Interest</>
            )}
          </Button>
          <Button onClick={onClose} variant="outline" className="rounded-none h-9 text-xs">
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};


// ─── Formal Offer Modal ───────────────────────────────────────────────────────

const OfferModal = ({ item, onSend, onClose }) => {
  const c = item.candidate || {};
  const [roleTitle, setRoleTitle] = useState(c.core_role || '');
  const [salaryRange, setSalaryRange] = useState('');
  const [contractType, setContractType] = useState('full-time');
  const [startDate, setStartDate] = useState('');
  const [personalMessage, setPersonalMessage] = useState('');
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!roleTitle.trim()) { toast.error('Please enter the role title'); return; }
    setSending(true);
    await onSend(item.id, {
      role_title: roleTitle.trim(),
      salary_range: salaryRange.trim(),
      contract_type: contractType,
      start_date: startDate,
      personal_message: personalMessage.trim(),
    });
    setSending(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="offer-modal">
      <div className="bg-white w-full max-w-lg border border-border" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
        <div className="px-6 py-4 border-b border-border flex items-start justify-between">
          <div>
            <h3 className="text-sm font-semibold" style={{ fontFamily: "'Bodoni Moda', Georgia, serif" }}>
              Send Formal Offer
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              {c.full_name} · {[c.seniority, c.core_role].filter(Boolean).join(' ')}
            </p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground mt-0.5">
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="bg-yellow-50 border border-yellow-100 px-4 py-3">
            <p className="text-xs text-yellow-800 leading-relaxed">
              This formal offer will appear in {c.full_name}&apos;s <strong>Offers tab</strong> on Flence. They can compare multiple offers and accept or decline from there.
            </p>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-widest mb-1.5">
              Role Title <span className="text-red-500">*</span>
            </label>
            <input type="text" value={roleTitle} onChange={e => setRoleTitle(e.target.value)}
              placeholder="e.g. Senior Womenswear Designer"
              className="w-full border border-border px-3 py-2 text-sm focus:outline-none focus:border-foreground"
              data-testid="offer-role-title-input" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest mb-1.5">Contract Type</label>
              <select value={contractType} onChange={e => setContractType(e.target.value)}
                className="w-full border border-border px-3 py-2 text-sm focus:outline-none focus:border-foreground bg-white"
                data-testid="offer-contract-type-select">
                <option value="full-time">Full-time</option>
                <option value="freelance">Freelance</option>
                <option value="contract">Contract</option>
                <option value="part-time">Part-time</option>
                <option value="internship">Internship</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-widest mb-1.5">Start Date</label>
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                className="w-full border border-border px-3 py-2 text-sm focus:outline-none focus:border-foreground"
                data-testid="offer-start-date-input" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-widest mb-1.5">Budget / Salary Range</label>
            <input type="text" value={salaryRange} onChange={e => setSalaryRange(e.target.value)}
              placeholder="e.g. €45,000 – €55,000 / year or €500/day"
              className="w-full border border-border px-3 py-2 text-sm focus:outline-none focus:border-foreground"
              data-testid="offer-salary-input" />
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-widest mb-1.5">Message to Candidate</label>
            <textarea value={personalMessage} onChange={e => setPersonalMessage(e.target.value)}
              placeholder="Any additional context about the role, team, or your expectations..."
              className="w-full border border-border px-3 py-2 text-sm resize-none focus:outline-none focus:border-foreground"
              rows={3} data-testid="offer-message-input" />
          </div>
        </div>

        <div className="px-6 pb-6 flex gap-2">
          <Button onClick={handleSend} disabled={sending || !roleTitle.trim()}
            className="flex-1 rounded-none bg-foreground text-background hover:bg-foreground/80 h-9 text-xs gap-1.5"
            data-testid="offer-send-btn">
            {sending ? (<><Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.5} /> Sending...</>)
              : (<><FileText className="w-3.5 h-3.5" strokeWidth={1.5} /> Send Formal Offer</>)}
          </Button>
          <Button onClick={onClose} variant="outline" className="rounded-none h-9 text-xs">Cancel</Button>
        </div>
      </div>
    </div>
  );
};


// ─── Kanban Card ─────────────────────────────────────────────────────────────

const KanbanCard = ({ item, index, onRemove, onNoteOpen, onOutreachOpen, onOfferOpen, onRestore, navigate }) => {
  const c = item.candidate || {};
  const stage = item.stage;
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Contextual action logic based on pipeline stage
  const getCardAction = () => {
    if (stage === 'archived') return { type: 'restore', label: 'RESTORE TO PIPELINE' };
    if (stage === 'hired' || stage === 'offer_sent') return null; // In flight — no further action
    if (stage === 'unavailable') return null; // Not reachable — no action
    if (stage === 'in_process') return { type: 'offer', label: 'SEND FORMAL OFFER', variant: 'gold' };
    if (stage === 'accepted') return { type: 'offer', label: 'SEND FORMAL OFFER', variant: 'gold' };
    if (stage === 'contacted' || item.outreach_sent_at) return { type: 'outreach', label: 'RESEND OUTREACH', variant: 'amber' };
    return { type: 'outreach', label: 'SEND OUTREACH', variant: 'dark' }; // saved — primary action
  };
  const action = getCardAction();

  return (
    <Draggable draggableId={item.id} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          data-testid={`pipeline-card-${item.id}`}
          className="bg-white border border-border p-3"
          style={{
            ...provided.draggableProps.style,
            boxShadow: snapshot.isDragging ? '0 4px 16px rgba(0,0,0,0.12)' : 'none',
            borderColor: snapshot.isDragging ? 'rgba(0,0,0,0.2)' : undefined,
            rotate: snapshot.isDragging ? '1deg' : '0deg',
          }}
        >
          <div className="flex items-start gap-2.5">
            {/* Drag handle */}
            <div
              {...provided.dragHandleProps}
              className="mt-0.5 flex-shrink-0 text-muted-foreground/30 hover:text-muted-foreground cursor-grab active:cursor-grabbing"
            >
              <GripVertical className="w-3 h-3" strokeWidth={1.5} />
            </div>

            {/* Photo */}
            <div
              className="flex-shrink-0 overflow-hidden bg-secondary cursor-pointer"
              style={{ width: 36, height: 48, filter: 'grayscale(0.6)', transition: 'filter 200ms' }}
              onMouseEnter={e => (e.currentTarget.style.filter = 'grayscale(0)')}
              onMouseLeave={e => (e.currentTarget.style.filter = 'grayscale(0.6)')}
              onClick={() => navigate(`/candidate/${c.id}`)}
            >
              {c.photo_url ? (
                <img src={c.photo_url} alt={c.full_name}
                  className="w-full h-full object-cover"
                  onError={e => { if (e.currentTarget) e.currentTarget.style.display='none'; }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <span className="text-xs font-semibold text-muted-foreground/50">
                    {c.full_name ? c.full_name.split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase() : '?'}
                  </span>
                </div>
              )}
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <button
                onClick={() => navigate(`/candidate/${c.id}`)}
                className="font-semibold text-xs leading-snug hover:underline text-left block truncate w-full"
                style={{ fontFamily: "'Bodoni Moda', Georgia, serif" }}
              >
                {c.full_name || 'Unknown'}
              </button>
              <p className="text-xs text-muted-foreground truncate mt-0.5 leading-tight">
                {[c.seniority, c.core_role].filter(Boolean).join(' ')}
              </p>
              {(c.location_city || c.location_country) && (
                <div className="flex items-center gap-0.5 mt-1 text-xs text-muted-foreground/60">
                  <MapPin className="w-2.5 h-2.5 flex-shrink-0" strokeWidth={1.5} />
                  <span className="truncate">{c.location_city || c.location_country}</span>
                </div>
              )}
              {c.overall_ranking && (
                <div className="flex items-center gap-0.5 mt-0.5">
                  <Star className="w-2.5 h-2.5 fill-amber-400 text-amber-400" strokeWidth={0} />
                  <span
                    className="text-xs text-muted-foreground"
                    style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                  >
                    {c.overall_ranking}
                  </span>
                </div>
              )}
            </div>

            {/* Actions — icon row */}
            <div className="flex flex-col gap-1 flex-shrink-0">
              <button
                onClick={() => onNoteOpen(item)}
                className="text-muted-foreground/40 hover:text-foreground p-0.5"
                title="Add note"
                data-testid={`note-btn-${item.id}`}
              >
                <StickyNote className="w-3 h-3" strokeWidth={1.5} />
              </button>
              {confirmDelete ? (
                <div className="flex gap-0.5">
                  <button
                    onClick={() => { onRemove(item.id); setConfirmDelete(false); }}
                    className="text-red-500 hover:text-red-700 p-0.5 text-xs font-medium"
                    title="Confirm remove"
                    data-testid={`confirm-remove-btn-${item.id}`}
                  >
                    <Trash2 className="w-3 h-3" strokeWidth={2} />
                  </button>
                  <button
                    onClick={() => setConfirmDelete(false)}
                    className="text-muted-foreground/60 hover:text-foreground p-0.5"
                    title="Cancel"
                    data-testid={`cancel-remove-btn-${item.id}`}
                  >
                    <X className="w-3 h-3" strokeWidth={1.5} />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setConfirmDelete(true)}
                  className="text-muted-foreground/40 hover:text-red-400 p-0.5"
                  title="Remove from pipeline"
                  data-testid={`remove-btn-${item.id}`}
                >
                  <Trash2 className="w-3 h-3" strokeWidth={1.5} />
                </button>
              )}
            </div>
          </div>

          {/* Notes preview */}
          {item.notes && (
            <p className="text-xs text-muted-foreground/70 mt-2 ml-5 pl-2 border-l border-border italic line-clamp-2">
              {item.notes}
            </p>
          )}

          {/* Email disclosed — show contact when candidate accepted */}
          {item.email_disclosed && item.candidate?.email && (
            <div className="flex items-center gap-1.5 mt-2 ml-5 px-2 py-1 bg-green-50 border border-green-100">
              <Mail className="w-2.5 h-2.5 text-green-600 flex-shrink-0" strokeWidth={1.5} />
              <a
                href={`mailto:${item.candidate.email}`}
                className="text-xs text-green-700 hover:underline truncate"
                onClick={e => e.stopPropagation()}
                data-testid={`disclosed-email-${item.id}`}
              >
                {item.candidate.email}
              </a>
            </div>
          )}

          {/* Send Outreach — contextual action button */}
          {action?.type === 'outreach' && action.variant === 'dark' && (
            <button onClick={() => onOutreachOpen(item)} data-testid={`outreach-btn-${item.id}`}
              className="mt-2.5 w-full flex items-center justify-center gap-1.5 px-2 py-1.5 bg-[#111] hover:bg-[#333] text-white transition-colors"
              style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '9px', letterSpacing: '0.12em' }}>
              <Send className="w-2.5 h-2.5" strokeWidth={1.5} />
              {action.label}
            </button>
          )}
          {action?.type === 'outreach' && action.variant === 'amber' && (
            <button onClick={() => onOutreachOpen(item)} data-testid={`outreach-btn-${item.id}`}
              className="mt-2.5 w-full flex items-center justify-center gap-1.5 px-2 py-1.5 border border-amber-200 bg-amber-50 hover:bg-amber-100 text-amber-700 transition-colors"
              style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '9px', letterSpacing: '0.12em' }}>
              <Mail className="w-2.5 h-2.5" strokeWidth={1.5} />
              {action.label}
            </button>
          )}
          {action?.type === 'outreach' && action.variant === 'success' && (
            <button onClick={() => onOutreachOpen(item)} data-testid={`outreach-btn-${item.id}`}
              className="mt-2.5 w-full flex items-center justify-center gap-1.5 px-2 py-1.5 border border-green-200 bg-green-50 hover:bg-green-100 text-green-700 transition-colors"
              style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '9px', letterSpacing: '0.12em' }}>
              <Mail className="w-2.5 h-2.5" strokeWidth={1.5} />
              {action.label}
            </button>
          )}
          {action?.type === 'restore' && (
            <button onClick={() => onRestore(item.id)} data-testid={`restore-btn-${item.id}`}
              className="mt-2.5 w-full flex items-center justify-center gap-1.5 px-2 py-1.5 border border-border hover:border-foreground text-muted-foreground hover:text-foreground transition-colors"
              style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '9px', letterSpacing: '0.12em' }}>
              <Plus className="w-2.5 h-2.5" strokeWidth={1.5} />
              {action.label}
            </button>
          )}
          {action?.type === 'offer' && (
            <button onClick={() => onOfferOpen(item)} data-testid={`offer-btn-${item.id}`}
              className="mt-2.5 w-full flex items-center justify-center gap-1.5 px-2 py-1.5 bg-[#c9a84c] hover:bg-[#b8963e] text-white transition-colors"
              style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '9px', letterSpacing: '0.12em' }}>
              <FileText className="w-2.5 h-2.5" strokeWidth={1.5} />
              {action.label}
            </button>
          )}
        </div>
      )}
    </Draggable>
  );
};

// ─── Kanban Column ────────────────────────────────────────────────────────────

const KanbanColumn = ({ stage, items, onRemove, onNoteOpen, onOutreachOpen, onOfferOpen, onRestore, navigate }) => {
  const cfg = STAGE_CONFIG[stage];

  return (
    <div className="flex-shrink-0 flex flex-col" style={{ width: 272 }} data-testid={`kanban-column-${stage}`}>
      {/* Header */}
      <div
        className="bg-white border border-border border-t-0 px-3 py-2.5 mb-2 flex items-center justify-between"
        style={{ borderTop: `2px solid ${cfg.topBorder}` }}
      >
        <span className="text-xs font-semibold uppercase tracking-widest">{cfg.label}</span>
        <span
          className={`text-xs px-1.5 py-0.5 font-medium ${cfg.badgeCls}`}
          style={{ fontFamily: "'IBM Plex Mono', monospace" }}
        >
          {items.length}
        </span>
      </div>

      {/* Drop zone */}
      <Droppable droppableId={stage}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className="flex-1 space-y-2 p-2 min-h-28"
            style={{
              backgroundColor: snapshot.isDraggingOver ? 'rgba(0,0,0,0.03)' : 'rgba(0,0,0,0.01)',
              transition: 'background-color 150ms',
            }}
          >
            {items.map((item, idx) => (
              <KanbanCard
                key={item.id}
                item={item}
                index={idx}
                onRemove={onRemove}
                onNoteOpen={onNoteOpen}
                onOutreachOpen={onOutreachOpen}
                onOfferOpen={onOfferOpen}
                onRestore={onRestore}
                navigate={navigate}
              />
            ))}
            {provided.placeholder}
            {items.length === 0 && !snapshot.isDraggingOver && (
              <div className="h-16 border border-dashed border-border flex items-center justify-center">
                <span className="text-xs text-muted-foreground/40 uppercase tracking-widest">Empty</span>
              </div>
            )}
          </div>
        )}
      </Droppable>
    </div>
  );
};

// ─── Note Modal ───────────────────────────────────────────────────────────────

const NoteModal = ({ item, initial, onSave, onClose }) => {
  const [text, setText] = useState(initial || '');
  const c = (item.candidate) || {};

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="note-modal">
      <div className="bg-white w-full max-w-sm border border-border">
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold">Recruiter Note</h3>
            <p className="text-xs text-muted-foreground mt-0.5">{c.full_name}</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
        <div className="p-5">
          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            className="w-full border border-border p-3 text-sm resize-none focus:outline-none focus:border-foreground"
            rows={4}
            placeholder="Interview notes, contact history, key impressions..."
            autoFocus
          />
          <div className="flex gap-2 mt-3">
            <Button
              onClick={() => onSave(text)}
              className="flex-1 rounded-none bg-foreground text-background hover:bg-foreground/80 h-9 text-xs"
              data-testid="save-note-btn"
            >
              Save Note
            </Button>
            <Button onClick={onClose} variant="outline" className="rounded-none h-9 text-xs">
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main Pipeline Component ──────────────────────────────────────────────────

const Pipeline = () => {
  const navigate = useNavigate();
  const [columns, setColumns] = useState(() =>
    STAGES.reduce((acc, s) => { acc[s] = []; return acc; }, {})
  );
  const [loading, setLoading] = useState(true);
  const [noteModal, setNoteModal] = useState(null);
  const [outreachModal, setOutreachModal] = useState(null);
  const [offerModal, setOfferModal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch(`${BACKEND}/api/dashboard/pipeline`, {});
      const d = await r.json();
      if (d.success) {
        const grouped = STAGES.reduce((acc, s) => { acc[s] = []; return acc; }, {});
        for (const item of d.data) {
          if (grouped[item.stage]) grouped[item.stage].push(item);
        }
        setColumns(grouped);
      }
    } catch {
      toast.error('Failed to load pipeline');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onDragEnd = useCallback((result) => {
    const { destination, source, draggableId } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;

    const newStage = destination.droppableId;

    setColumns(prev => {
      const next = {};
      for (const s of STAGES) next[s] = [...prev[s]];
      const [moved] = next[source.droppableId].splice(source.index, 1);
      moved.stage = newStage;
      next[destination.droppableId].splice(destination.index, 0, moved);
      return next;
    });

    authFetch(`${BACKEND}/api/dashboard/pipeline/${draggableId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ stage: newStage }),
    }).catch(() => { toast.error('Failed to update stage'); load(); });
  }, [load]);

  const removeItem = async (id) => {
    setColumns(prev => {
      const next = {};
      for (const s of STAGES) next[s] = prev[s].filter(i => i.id !== id);
      return next;
    });
    await authFetch(`${BACKEND}/api/dashboard/pipeline/${id}`, { method: 'DELETE' });
    toast.success('Removed from pipeline');
  };

  const saveNote = async (text) => {
    if (!noteModal) return;
    await authFetch(`${BACKEND}/api/dashboard/pipeline/${noteModal.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ notes: text }),
    });
    setColumns(prev => {
      const next = {};
      for (const s of STAGES) next[s] = prev[s].map(i => i.id === noteModal.id ? { ...i, notes: text } : i);
      return next;
    });
    setNoteModal(null);
    toast.success('Note saved');
  };

  const sendOutreach = async (pipelineId, payload) => {
    try {
      const r = await authFetch(`${BACKEND}/api/dashboard/pipeline/${pipelineId}/send-outreach`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const d = await r.json();
      if (d.success) {
        toast.success('Interest expressed — candidate will be notified via Flence');
        setOutreachModal(null);
        // Move card to "contacted" in UI
        setColumns(prev => {
          const next = {};
          for (const s of STAGES) next[s] = [...prev[s]];
          let moved = null;
          for (const s of STAGES) {
            const idx = next[s].findIndex(i => i.id === pipelineId);
            if (idx !== -1) { [moved] = next[s].splice(idx, 1); break; }
          }
          if (moved) { moved.stage = 'contacted'; next['contacted'].unshift(moved); }
          return next;
        });
      } else {
        toast.error(d.detail || 'Failed to send email');
      }
    } catch {
      toast.error('Failed to send outreach email');
    }
  };

  const sendOffer = async (pipelineId, payload) => {
    try {
      const r = await authFetch(`${BACKEND}/api/dashboard/pipeline/${pipelineId}/send-offer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const d = await r.json();
      if (d.success) {
        toast.success('Formal offer sent — candidate will see it in Flence');
        setOfferModal(null);
        setColumns(prev => {
          const next = {};
          for (const s of STAGES) next[s] = [...prev[s]];
          let moved = null;
          for (const s of STAGES) {
            const idx = next[s].findIndex(i => i.id === pipelineId);
            if (idx !== -1) { [moved] = next[s].splice(idx, 1); break; }
          }
          if (moved) { moved.stage = 'offer_sent'; next['offer_sent'].unshift(moved); }
          return next;
        });
      } else {
        toast.error(d.detail || 'Failed to send offer');
      }
    } catch {
      toast.error('Failed to send formal offer');
    }
  };

  const restoreItem = async (id) => {
    setColumns(prev => {
      const next = {};
      for (const s of STAGES) next[s] = prev[s].map(i => i.id === id ? { ...i, stage: 'saved' } : i);
      // Move from archived to saved
      const item = Object.values(next).flat().find(i => i.id === id);
      if (item) {
        for (const s of STAGES) next[s] = next[s].filter(i => i.id !== id);
        item.stage = 'saved';
        next['saved'].unshift(item);
      }
      return next;
    });
    await authFetch(`${BACKEND}/api/dashboard/pipeline/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ stage: 'saved' }),
    });
    toast.success('Restored to Saved');
  };

  const totalCount = STAGES.reduce((sum, s) => sum + columns[s].length, 0);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-56 bg-secondary animate-pulse" />
        <div className="flex gap-4 overflow-x-auto pb-4">
          {STAGES.map(s => (
            <div key={s} className="flex-shrink-0 w-[272px]">
              <div className="h-10 bg-white border border-border animate-pulse mb-2" />
              <div className="space-y-2">
                {[1,2,3].map(i => <div key={i} className="h-20 bg-white border border-border animate-pulse" />)}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1
            className="text-xl sm:text-2xl font-semibold tracking-tight"
            style={{ fontFamily: "'Bodoni Moda', Georgia, serif" }}
            data-testid="pipeline-heading"
          >
            Candidate Pipeline
          </h1>
          <p
            className="text-xs text-muted-foreground mt-1 uppercase tracking-[0.15em]"
            style={{ fontFamily: "'IBM Plex Mono', monospace" }}
          >
            {totalCount} candidate{totalCount !== 1 ? 's' : ''} · drag to advance stages
          </p>
        </div>
        <Button
          onClick={() => navigate('/gallery')}
          className="rounded-none bg-foreground text-background hover:bg-foreground/80 gap-1.5 h-9 text-xs flex-shrink-0"
          data-testid="add-from-gallery-btn"
        >
          <Plus className="w-3.5 h-3.5" strokeWidth={1.5} />
          <span className="hidden sm:inline">Add from Gallery</span>
          <span className="sm:hidden">Add</span>
        </Button>
        <PageHelp pageKey="pipeline" />
      </div>

      {/* Empty state */}
      {totalCount === 0 ? (
        <div className="text-center py-24 bg-white border border-border" data-testid="pipeline-empty">
          <Users className="w-8 h-8 text-muted-foreground/30 mx-auto mb-4" strokeWidth={1.5} />
          <p className="font-semibold text-sm mb-1">Pipeline is empty</p>
          <p className="text-xs text-muted-foreground mb-5">
            Browse the gallery and save candidates to begin tracking.
          </p>
          <Button
            onClick={() => navigate('/gallery')}
            variant="outline"
            className="rounded-none text-xs h-8"
          >
            Browse Gallery
          </Button>
        </div>
      ) : (
        <DragDropContext onDragEnd={onDragEnd}>
          <div
            className="flex gap-4 overflow-x-auto pb-6"
            style={{ marginLeft: -2, paddingLeft: 2 }}
            data-testid="kanban-board"
          >
            {STAGES.map(stage => (
              <KanbanColumn
                key={stage}
                stage={stage}
                items={columns[stage]}
                onRemove={removeItem}
                onNoteOpen={setNoteModal}
                onOutreachOpen={setOutreachModal}
                onOfferOpen={setOfferModal}
                onRestore={restoreItem}
                navigate={navigate}
              />
            ))}
          </div>
        </DragDropContext>
      )}

      {/* Note modal */}
      {noteModal && (
        <NoteModal
          item={noteModal}
          initial={noteModal.notes || ''}
          onSave={saveNote}
          onClose={() => setNoteModal(null)}
        />
      )}

      {/* Outreach email modal */}
      {outreachModal && (
        <OutreachModal
          item={outreachModal}
          onSend={sendOutreach}
          onClose={() => setOutreachModal(null)}
        />
      )}

      {/* Formal offer modal */}
      {offerModal && (
        <OfferModal
          item={offerModal}
          onSend={sendOffer}
          onClose={() => setOfferModal(null)}
        />
      )}
    </div>
  );
};

export default Pipeline;
